import styled from '@emotion/styled';
import { Collapse } from 'antd';
import { ModalBody } from 'reactstrap';
import React from 'react';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  ClockCircleFilled,
} from '@ant-design/icons';

// Layout & structure
export const PageShell = styled.div`
  background: #f4f6f8;
  min-height: 100vh;
  padding: 24px 0;
  font-family: 'Inter', sans-serif;
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Summary Section
export const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 16px;
`;

export const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

export const SummarySubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #6b7280;
  font-weight: 600;
  pointer-events: none;
`;

export const ScoreGradient = styled.div`
  color: #4b5563;
  font-size: 48px;
  font-weight: 900;
  line-height: 1;
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

export const StatTile = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StatIconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eef2ff;
  color: #767676;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Tables & Actions
export const TableCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .table-header-custom {
    margin-bottom: 0;

    th {
      background: #f9fafb;
      color: #374151;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 12px;
      letter-spacing: 0.5px;
      border-bottom: 2px solid #e5e7eb;
      padding: 16px;
    }

    td {
      padding: 16px;
      vertical-align: middle;
      color: #4b5563;
      border-bottom: 1px solid #f3f4f6;
    }

    tbody tr {
      transition: background-color 0.2s ease;
      &:hover {
        background-color: #f9fafb;
      }
    }
  }
`;

export const EyeActionButton = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

export const HistoryStatusCell = styled.td`
  color: ${({ $color }) => $color || '#6b7280'};
  font-weight: 600;
`;

export const CursorPointer = styled.span`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

export const ScrollableModalBody = styled(ModalBody)`
  max-height: min(75vh, 640px);
  overflow-y: auto;
  padding: 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

// Microlearning & Breakdown Sections
export const SectionBlock = styled.div`
  margin-top: 24px;
`;

export const StyledCollapse = styled(Collapse)`
  background: transparent !important;

  .ant-collapse-item {
    background: #ffffff;
    border-radius: 12px !important;
    border: 1px solid #e5e7eb;
    margin-bottom: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  }

  .ant-collapse-header {
    padding: 16px 24px !important;
    align-items: center !important;
  }
`;

export const BorderlessPanel = styled(Collapse.Panel)`
  border: none !important;
`;

export const MicroHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const MicroIconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fff7ed;
  color: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

export const MicroTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

export const AiPill = styled.span`
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const MicroBody = styled.div`
  padding: 0 24px 24px 24px;
  color: #4b5563;
  line-height: 1.6;
`;

export const BreakdownHeading = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 32px 0 16px 0;
`;

export const QuestionCollapse = styled(StyledCollapse)`
  .ant-collapse-item {
    border-left: 4px solid #3b82f6;
  }
`;

export const QuestionPanel = styled(Collapse.Panel)`
  border: none !important;

  .ant-collapse-content {
    border-top: 1px solid #f3f4f6;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

export const QHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const QNumberLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 6px;
`;

export const QPreviewText = styled.span`
  font-size: 14px;
  color: #4b5563;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Helper functions for Weight Pill status
export const getWeightTone = (status) => {
  return { bg: '#f3f4f6', color: '#374151' };
};

export const WeightPillLabel = styled.span``;

export const WeightPill = styled.div`
  background: ${({ $bg }) => $bg || '#f3f4f6'};
  color: ${({ $color }) => $color || '#374151'};
  font-size: 13px;
  font-weight: 700;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const QuestionBody = styled.div`
  padding: 24px;
  color: #1f2937;
  font-size: 15px;
  line-height: 1.6;
`;

export const QuestionAnswerSection = styled.div`
  background: #f8fafc;
  padding: 24px;
  border-top: 1px dashed #e2e8f0;
`;

export const EmptyStateCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 16px;
  font-weight: 500;
  border: 2px dashed #e5e7eb;
`;

// Status Badge Component
const BadgeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${({ $color }) => $color};
`;

export const QuestionStatusBadge = ({ status }) => {
  let icon = <MinusCircleFilled />;
  let color = '#9ca3af';

  switch (status) {
    case 'COMPLETED':
    case 'PASSED':
      icon = <CheckCircleFilled />;
      color = '#10b981';
      break;
    case 'FAILED':
      icon = <CloseCircleFilled />;
      color = '#ef4444';
      break;
    case 'PENDING':
      icon = <ClockCircleFilled />;
      color = '#f59e0b';
      break;
    default:
      break;
  }

  return <BadgeWrapper $color={color}>{icon}</BadgeWrapper>;
};
